import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Swiper } from 'swiper/react';
import SwiperCore, { Scrollbar } from 'swiper/core';
import 'swiper/swiper.scss';
import 'swiper/components/scrollbar/scrollbar.scss';
import { SwiperSlide } from 'swiper/react';
import { LoadingMoreSpinner } from './SmallUIComponents';
import { pushEventToGTMDataLayer } from 'src/scripts/utils/gtm-data-layer';

SwiperCore.use([Scrollbar]);

export const PlpTileScrollableImages = ({
  primaryImageUrl,
  secondaryImageUrl,
  fetchAdditionalImages,
  productTitle,
  productID,
  collectionHandle,
  plpTileIndex,
  firstTileVisible,
}: {
  primaryImageUrl: string;
  secondaryImageUrl: string | null;
  fetchAdditionalImages: (primaryImageUrl: string, secondaryImageUrl: string | null) => Promise<string[]>;
  productTitle: string;
  productID: string;
  collectionHandle: string | undefined;
  plpTileIndex: number;
  firstTileVisible: boolean;
}) => {
  const isFirstPlpTile = plpTileIndex === 0;
  const [swiperKey, setSwiperKey] = useState(0);
  const [imagesToDisplay, setImagesToDisplay] = useState<string[]>([]);
  const [extendedImagesAdded, setExtendedImagesAdded] = useState<boolean>(false);

  const [imageLoadStatus, setImageLoadStatus] = useState<Record<string, string>>({});
  const getImageLoadingStatuses = (imageUrls: string[]): Record<string, string> => {
    const imageLoadStatus: Record<string, string> = {};
    imageUrls.forEach((imageUrl) => {
      imageLoadStatus[imageUrl] = 'loading';
    });
    return imageLoadStatus;
  };

  useEffect(() => {
    const initialImagesToDisplay = [primaryImageUrl, secondaryImageUrl]
      .filter((imageUrl): imageUrl is string => imageUrl !== null)
      .map((imageUrl) => imageUrl as string);
    setImagesToDisplay(initialImagesToDisplay);
    setSwiperKey((prevKey) => prevKey + 1);
    setExtendedImagesAdded(false);
    setImageLoadStatus(getImageLoadingStatuses(initialImagesToDisplay));
  }, [primaryImageUrl, secondaryImageUrl]);

  // need to manage the animation lifecycle so it only runs once
  type AnimationStates = 'not run' | 'triggered' | 'done';
  const [animationState, setAnimationState] = useState<AnimationStates>('not run');
  const animationDelay = 700; // once the tile is visible, don't immediately animate
  const animationRunTime = animationDelay + 1100; // animation runs for 1.1s
  const changeAnimationState = useCallback(
    (setState: Dispatch<SetStateAction<AnimationStates>>, state: AnimationStates, delay: number) => {
      setTimeout(() => {
        setState(state);
      }, delay);
    },
    []
  );

  useEffect(() => {
    const animationShouldBeTriggered = isFirstPlpTile && firstTileVisible && imagesToDisplay.length > 1;
    if (animationShouldBeTriggered && animationState === 'not run') {
      changeAnimationState(setAnimationState, 'triggered', animationDelay);
      changeAnimationState(setAnimationState, 'done', animationRunTime);
    }
  }, [
    firstTileVisible,
    imagesToDisplay.length,
    isFirstPlpTile,
    animationState,
    changeAnimationState,
    animationRunTime,
  ]);

  const sendSwipeTrackingEvents = (activeIndex: number) => {
    const data = {
      productTitle: productTitle,
      productID: productID,
      collectionHandle: collectionHandle,
      slideIndex: activeIndex + 1, // so we have numbers 1-6 instead of 0-5
    };
    try {
      heap.track('plpTileSwipeEvent', { data: data });
      pushEventToGTMDataLayer({ event: 'plpTileSwipeEvent', data: data });
    } catch (error) {
      console.log(error);
    }
  };

  const handleSlideChange = async (swiper: SwiperCore) => {
    const { activeIndex } = swiper;
    const { isEnd } = swiper;
    if (isEnd && !extendedImagesAdded) {
      try {
        const additionalImageUrls = await fetchAdditionalImages(primaryImageUrl, secondaryImageUrl);
        if (additionalImageUrls && additionalImageUrls.length > 0) {
          setImagesToDisplay((prevImages) => [...prevImages, ...additionalImageUrls]);
          setExtendedImagesAdded(true);
          const additionalImageLoadingStatus = getImageLoadingStatuses(additionalImageUrls);
          setImageLoadStatus((prevStatus) => ({ ...prevStatus, ...additionalImageLoadingStatus }));
        } else {
          console.error('Additional image URLs not found or empty.');
        }
      } catch (error) {
        console.error('Error fetching additional images:', error);
      }
    }
    sendSwipeTrackingEvents(activeIndex);
  };

  const handleImageLoad = (imageUrl: string) => {
    setImageLoadStatus((prevStatus) => ({
      ...prevStatus,
      [imageUrl]: 'loaded',
    }));
  };

  const handleImageError = (imageUrl: string) => {
    setImageLoadStatus((prevStatus) => ({
      ...prevStatus,
      [imageUrl]: 'error',
    }));
  };

  return (
    <Swiper
      key={swiperKey}
      spaceBetween={0}
      slidesPerView={1}
      speed={200}
      scrollbar={{ snapOnRelease: true, hide: true }}
      allowTouchMove
      onSlideChange={(swiper) => handleSlideChange(swiper)}
      className={`scrollable-images ${animationState === 'triggered' ? `animate-tile` : ''}`}
    >
      {imagesToDisplay.map((imageUrl: string) => (
        <SwiperSlide key={imageUrl}>
          {imageLoadStatus[imageUrl] === 'loading' && <LoadingMoreSpinner className="image-loading-spinner" />}
          <img
            className={`scrollable-image ${imageLoadStatus[imageUrl]}`}
            alt={productTitle}
            src={imageUrl}
            onLoad={() => handleImageLoad(imageUrl)}
            onError={() => handleImageError(imageUrl)}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};
